// Host-page dataLayer bridge — pushes named events under stable,
// destination-namespaced names so CC's GTM Web Container can re-fire
// the legacy Facebook Pixel and Trade Desk tags on widget activity.
//
// Why a separate event namespace? Pushing generic `event: 'booking'`
// would also wake up the dormant Web Container tags `Booking 1..5
// (booking)`, `Reservation Completed (Facebook Event)` listening on
// `booking step=5`, `Google Ads Conversion - Booking Confirmation`,
// `Buffalo Group PPC Pixel - Confirmation`, and `ChannelMix Conversion`
// — every one of those would duplicate-fire alongside the equivalent
// tag already running in our sGTM workspace. Using a `cc_widget_*`
// namespace that nothing currently listens for makes the surface
// surgical: only the four tags we explicitly re-trigger fire.
//
// Event → Web Container tag map:
//
//   cc_widget_booking_lead      ← book_flow_submit
//                                  Fires: Facebook Pixel
//                                         (Main Site Form Submission) AND
//                                         Microsoft Ads Enhanced Conversion
//
//   cc_widget_booking_begin     ← book_flow_fitting_type_selected
//                                  Fires: TTD Begin Booking
//
//   cc_widget_booking_complete  ← book_flow_confirm_a_number
//                                  Fires: Reservation Completed
//                                         (Facebook Event) AND
//                                         TTD Complete Booking
//
// (V3 renamed book_flow_contact_info_complete → book_flow_submit,
// book_flow_loc_type_date_selected → book_flow_fitting_type_selected, and
// book_flow_complete → book_flow_confirm_a_number. The cc_widget_* names
// and their payloads are unchanged.)
//
// Set window.CC_WIDGET_CONFIG.hostPixelDispatch = false on the host
// page to disable (dev env, pre-Web-Container-cutover, etc.).

import type { EventParams } from './events';

function pushDataLayerEvent(event: string, extras: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  if (window.CC_WIDGET_CONFIG?.hostPixelDispatch === false) return;
  window.dataLayer = window.dataLayer || [];
  const out: Record<string, unknown> = { event };
  for (const [k, v] of Object.entries(extras)) {
    if (v !== undefined) out[k] = v;
  }
  window.dataLayer.push(out);
}

export function maybeEmitHostPixels(payload: EventParams): void {
  const p = payload as unknown as Record<string, unknown>;
  switch (payload.event) {
    case 'book_flow_submit':
      pushDataLayerEvent('cc_widget_booking_lead', {
        form_id: 'CMwebsubmit',
        form_name: 'CC Booking Widget',
      });
      break;
    case 'book_flow_fitting_type_selected':
      pushDataLayerEvent('cc_widget_booking_begin', {
        location: p.location,
        fitting_selection: p.fitting_selection,
      });
      break;
    case 'book_flow_confirm_a_number':
      pushDataLayerEvent('cc_widget_booking_complete', {
        confirmation_number: p.confirmation_number,
        value: p.value,
        currency: p.currency,
        store: p.store,
      });
      break;
  }
}
